import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';

export const HeroSection: FC = () => {
  return (
    <section className="relative overflow-hidden bg-white pt-20 pb-28">
      {/* 背景装饰：极淡的放射渐变 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(0,113,227,0.06) 0%, transparent 70%)',
        }}
      />

      <Container>
        {/* 顶部小标签 */}
        <div className="flex justify-center mb-6">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#EBF5FF] text-apple-blue text-sm font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-apple-blue inline-block" />
            基于 Claude 最新技术
          </span>
        </div>

        {/* 主标题 */}
        <div className="text-center max-w-4xl mx-auto">
          <h1
            className="font-bold text-apple-dark mb-6"
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
            }}
          >
            智能编程的
            <br />
            <span className="text-gradient">新时代已到来</span>
          </h1>

          <p
            className="text-apple-gray mx-auto mb-10 max-w-2xl"
            style={{ fontSize: '1.25rem', lineHeight: 1.6, letterSpacing: '-0.01em' }}
          >
            白茆AI 将 Claude 的强大能力带给每一位中国开发者——代码生成、审查、重构，全面提升开发效率。
          </p>

          {/* CTA 按钮组 */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-16">
            <Button as={Link} to="/docs" size="lg">
              立即安装
            </Button>
            <Button as={Link} to="/pricing" variant="outline" size="lg">
              查看方案
            </Button>
          </div>
        </div>

        {/* 代码演示窗口 */}
        <div className="relative max-w-3xl mx-auto">
          {/* 窗口阴影光晕 */}
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-apple-xl opacity-40 blur-2xl"
            style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(0,113,227,0.15), transparent 70%)' }}
          />

          <div className="relative bg-[#1C1C1E] rounded-apple-lg shadow-apple-xl overflow-hidden">
            {/* 窗口标题栏 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#FF5F56]" />
                <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
              </div>
              <span className="text-xs text-white/40 font-mono">baimao-ai — zsh</span>
              <div className="w-14" />
            </div>

            {/* 终端内容 */}
            <div className="p-5 font-mono text-sm leading-relaxed">
              <div className="text-[#98CEFF] mb-3">
                <span className="text-white/40 select-none">❯ </span>
                baimao "帮我写一个高性能的快速排序"
              </div>
              <div className="text-white/50 mb-4 text-xs">
                ✦ 正在生成 Python 快速排序函数...
              </div>
              <pre
                className="text-[13px] overflow-x-auto rounded-apple"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  padding: '1rem',
                  color: '#e2e8f0',
                  lineHeight: 1.7,
                }}
              >
{`def quicksort(arr: list) -> list:
    if len(arr) <= 1:
        return arr

    pivot = arr[len(arr) // 2]
    left   = [x for x in arr if x < pivot]
    middle = [x for x in arr if x == pivot]
    right  = [x for x in arr if x > pivot]

    return quicksort(left) + middle + quicksort(right)`}
              </pre>
              <div className="mt-4 flex items-center gap-2 text-[#34C759] text-xs">
                <span>✓</span>
                <span>生成完成，已添加类型注解和内联注释</span>
              </div>
            </div>
          </div>

          {/* 浮动信息卡片 */}
          <div className="absolute -top-3 -right-3 glass rounded-apple shadow-apple-md px-3.5 py-2.5 hidden lg:block">
            <div className="text-[11px] text-apple-gray mb-0.5">支持语言</div>
            <div className="text-sm font-semibold text-apple-dark tracking-tight">
              Python · JS · Go · Java
            </div>
          </div>
          <div className="absolute -bottom-3 -left-3 glass rounded-apple shadow-apple-md px-3.5 py-2.5 hidden lg:block">
            <div className="text-[11px] text-apple-gray mb-0.5">平均响应</div>
            <div className="text-sm font-semibold text-apple-blue tracking-tight">&lt; 2 秒</div>
          </div>
        </div>
      </Container>
    </section>
  );
};
